<template>
  <Modal title="" :max-width="720" :header-border="false" body-overflow="auto" body-max-height="600px" :body-padding="false" @close="onClose">
    <template #header>
      <div class="cd__title-wrap">
        <span class="cd-source">
          <span class="cd-source__mark" :style="sourceStyle">
            <component :is="sourceIcon" />
          </span>
          {{ sourceLabel }}
        </span>
        <div class="cd__name">{{ subjectDisplay }}</div>
        <div class="cd__sub">
          <template v-for="(seg, i) in subSegments" :key="i">
            <span v-if="i > 0" class="cd__dot" />
            <span>{{ seg }}</span>
          </template>
        </div>
      </div>
    </template>

    <div v-if="loading" class="cd-loading">Loading...</div>
    <div v-else-if="!emailData" class="cd-loading">Email not found.</div>
    <div v-else class="cd-email-body">
      <div class="cd-prose">{{ bodyDisplay }}</div>
    </div>

    <template #footer>
      <button class="cd-btn cd-btn--ghost" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, h, ref, watch } from 'vue'
import Modal from '../../next/components/Modal.vue'
import { apiFetch } from '../../services/api'
import { useContextDrawer } from '../useContextDrawer'

const { contextGeneration } = useContextDrawer()

const props = defineProps<{
  /** Timeline item type (salesforce_email_in, hubspot_email_out, pipedrive_email_in, etc.) */
  itemType: string
  /** Account ID for API fetch (SF emails) */
  accountId?: string
  /** Email record ID for API fetch (SF emails) */
  emailId?: string
  /** Inline email data from the timeline item */
  initialData?: Record<string, any> | null
  onClose: () => void
}>()

const loading = ref(false)
const emailData = ref<Record<string, any> | null>(props.initialData || null)

// ── CRM source detection ────────────────────────────────────────

type CrmSource = 'salesforce' | 'hubspot' | 'pipedrive'

const crmSource = computed<CrmSource>(() => {
  const t = props.itemType || ''
  if (t.startsWith('hubspot_')) return 'hubspot'
  if (t.startsWith('pipedrive_')) return 'pipedrive'
  return 'salesforce'
})

const isInbound = computed(() => {
  const t = props.itemType || ''
  if (t.endsWith('_in')) return true
  if (t.endsWith('_out')) return false
  // SF generic: check direction field
  const dir = String(emailData.value?.direction || '').toLowerCase()
  return dir === 'in' || dir === 'inbound'
})

// ── Source badge ────────────────────────────────────────────────

const sourceLabel = computed(() => {
  if (crmSource.value === 'hubspot') return 'HubSpot'
  if (crmSource.value === 'pipedrive') return 'Pipedrive'
  return 'Salesforce'
})

const sourceStyle = computed(() => {
  if (crmSource.value === 'hubspot') return { background: '#ff7a59', color: '#fff' }
  if (crmSource.value === 'pipedrive') return { background: '#017737', color: '#fff' }
  return { background: '#0d9dda', color: '#fff' }
})

// SVG icons as render functions
const sfIcon = () => h('svg', { viewBox: '0 0 16 16', fill: 'currentColor' }, [
  h('path', { d: 'M6.5 4.2a3 3 0 015.4 1 2.4 2.4 0 011.6 4.3 2.6 2.6 0 01-3 2 3.1 3.1 0 01-5.2-.4 2.3 2.3 0 01-2-3.9 2.7 2.7 0 013.2-3z' }),
])
const hsIcon = () => h('svg', { viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.6' }, [
  h('circle', { cx: '6', cy: '10', r: '2.4' }),
  h('circle', { cx: '11.5', cy: '5.5', r: '1.6' }),
  h('path', { d: 'M7.4 8.4l3-1.8M11.5 7.1v2', 'stroke-linecap': 'round' }),
])
const pdIcon = () => h('svg', { viewBox: '0 0 16 16', fill: 'currentColor' }, [
  h('path', { d: 'M8 2a4 4 0 014 4v2.5a1.5 1.5 0 01-1.5 1.5h-5A1.5 1.5 0 014 8.5V6a4 4 0 014-4zm0 9c-1.3 0-2.4.8-2.8 2h5.6A3 3 0 008 11z' }),
])

const sourceIcon = computed(() => {
  if (crmSource.value === 'hubspot') return hsIcon
  if (crmSource.value === 'pipedrive') return pdIcon
  return sfIcon
})

// ── Subject ────────────────────────────────────────────────────

const subjectDisplay = computed(() => {
  const d = emailData.value
  if (!d) return 'Email'
  return String(d.clean_subject || d.subject || 'Email').trim() || 'Email'
})

// ── Sub-segments (breadcrumb line) ─────────────────────────────

const subSegments = computed(() => {
  const parts: string[] = []
  parts.push(isInbound.value ? 'Received' : 'Sent')
  const d = emailData.value
  if (d) {
    // Attempt to find a date
    const rawDate = d.created_date || d.activity_date || d.occurred_at || d.hs_timestamp || d.message_time
    if (rawDate) {
      try {
        const dt = new Date(rawDate)
        parts.push(dt.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }))
        parts.push(dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }))
      } catch { /* skip */ }
    }
  }
  return parts
})

// ── Body ───────────────────────────────────────────────────────

const bodyDisplay = computed(() => {
  const d = emailData.value
  if (!d) return ''
  // Prefer the processed body (which includes natural From:/To: headers from
  // the email), falling back through other body fields.
  return String(d.body || d.text_body || d.body_preview || d.snippet || d.preview || d.description || '').trim()
})

// ── API fetch for Salesforce emails ────────────────────────────

async function loadEmail() {
  const accountId = String(props.accountId || '').trim()
  const emailId = String(props.emailId || '').trim()
  if (!accountId || !emailId) return
  // If we already have the full data, skip
  if (props.initialData && String(props.initialData?.id || '').trim() === emailId) {
    emailData.value = props.initialData
    return
  }
  const gen = contextGeneration.value
  loading.value = true
  try {
    const resp = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/salesforce_emails/${encodeURIComponent(emailId)}`)
    if (gen !== contextGeneration.value) return
    emailData.value = resp?.email || null
  } catch {
    if (gen !== contextGeneration.value) return
    emailData.value = null
  } finally {
    if (gen === contextGeneration.value) {
      loading.value = false
    }
  }
}

watch(() => [props.accountId, props.emailId], () => {
  if (crmSource.value === 'salesforce') {
    void loadEmail()
  }
}, { immediate: true })

// For HS/PD, data is passed inline — no API fetch needed
if (crmSource.value !== 'salesforce' && props.initialData) {
  emailData.value = props.initialData
}
</script>

<style scoped>
.cd-loading {
  padding: var(--space-5);
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
}

.cd-email-body {
  display: flex;
  flex-direction: column;
}

.cd-prose {
  padding: var(--space-4) var(--space-5) var(--space-5);
  font-size: var(--fs-base);
  line-height: 1.7;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 60px;
}
</style>
